import React, { useState, useEffect } from 'react';
import PrimaryButton from '../../Common/PrimaryButton';

/**
 * Formulário para criar ou editar exame
 */
const ExameForm = ({ onClose, onSubmit, initialData = {} }) => {
    const isEditing = !!initialData.id;
    const [formData, setFormData] = useState({
        tipo_exame: 'laboratorial',
        nome_exame: '',
        data_realizacao: '',
        data_resultado: '',
        resultado: '',
        local_realizacao: '',
        medico_solicitante: '',
        anexo_url: '',
        status: 'pendente',
        valor_referencia: '',
        interpretacao: '',
        observacoes: ''
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                tipo_exame: initialData.tipo_exame || 'laboratorial',
                nome_exame: initialData.nome_exame || '',
                data_realizacao: initialData.data_realizacao 
                    ? new Date(initialData.data_realizacao).toISOString().substring(0, 10) 
                    : '',
                data_resultado: initialData.data_resultado 
                    ? new Date(initialData.data_resultado).toISOString().substring(0, 10) 
                    : '',
                resultado: initialData.resultado || '',
                local_realizacao: initialData.local_realizacao || '',
                medico_solicitante: initialData.medico_solicitante || '',
                anexo_url: initialData.anexo_url || '',
                status: initialData.status || 'pendente',
                valor_referencia: initialData.valor_referencia || '',
                interpretacao: initialData.interpretacao || '',
                observacoes: initialData.observacoes || ''
            });
        }
        setFormError(null);
    }, [initialData, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.nome_exame.trim()) {
            setFormError('O nome do exame é obrigatório.');
            return;
        }

        if (formData.data_resultado && formData.data_realizacao) {
            const realizacao = new Date(formData.data_realizacao);
            const resultado = new Date(formData.data_resultado);
            if (resultado < realizacao) {
                setFormError('A data do resultado não pode ser anterior à data de realização.');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            setFormError(error?.response?.data?.message || error?.message || 'Erro ao salvar exame.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm" role="alert">
                    {formError}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="tipo_exame" className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Exame <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="tipo_exame"
                        name="tipo_exame"
                        value={formData.tipo_exame}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        required
                    >
                        <option value="laboratorial">Laboratorial</option>
                        <option value="imagem">Imagem</option>
                        <option value="outro">Outro</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                    </label>
                    <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    >
                        <option value="pendente">Pendente</option>
                        <option value="realizado">Realizado</option>
                        <option value="cancelado">Cancelado</option>
                    </select>
                </div>
            </div>

            <div>
                <label htmlFor="nome_exame" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Exame <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="nome_exame"
                    name="nome_exame"
                    value={formData.nome_exame}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ex: Hemograma, Raio-X de Tórax, Ultrassom..."
                    required
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="data_realizacao" className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Realização
                    </label>
                    <input
                        type="date"
                        id="data_realizacao"
                        name="data_realizacao"
                        value={formData.data_realizacao}
                        onChange={handleChange}
                        max={new Date().toISOString().substring(0, 10)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                <div>
                    <label htmlFor="data_resultado" className="block text-sm font-medium text-gray-700 mb-1">
                        Data do Resultado
                    </label>
                    <input
                        type="date"
                        id="data_resultado"
                        name="data_resultado"
                        value={formData.data_resultado}
                        onChange={handleChange}
                        max={new Date().toISOString().substring(0, 10)}
                        min={formData.data_realizacao || ''}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="local_realizacao" className="block text-sm font-medium text-gray-700 mb-1">
                        Local de Realização
                    </label>
                    <input
                        type="text"
                        id="local_realizacao"
                        name="local_realizacao"
                        value={formData.local_realizacao}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Ex: Laboratório X, Clínica Y..."
                    />
                </div>

                <div>
                    <label htmlFor="medico_solicitante" className="block text-sm font-medium text-gray-700 mb-1">
                        Médico Solicitante
                    </label>
                    <input
                        type="text"
                        id="medico_solicitante"
                        name="medico_solicitante"
                        value={formData.medico_solicitante}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Nome do médico..."
                    />
                </div>
            </div>

            <div>
                <label htmlFor="resultado" className="block text-sm font-medium text-gray-700 mb-1">
                    Resultado
                </label>
                <textarea
                    id="resultado"
                    name="resultado"
                    value={formData.resultado}
                    onChange={handleChange}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Resultado do exame..."
                />
            </div>

            <div>
                <label htmlFor="valor_referencia" className="block text-sm font-medium text-gray-700 mb-1">
                    Valores de Referência
                </label>
                <textarea
                    id="valor_referencia"
                    name="valor_referencia"
                    value={formData.valor_referencia}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Valores de referência para o exame..."
                />
            </div>

            <div>
                <label htmlFor="interpretacao" className="block text-sm font-medium text-gray-700 mb-1">
                    Interpretação
                </label>
                <textarea
                    id="interpretacao"
                    name="interpretacao"
                    value={formData.interpretacao}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Interpretação do resultado pelo médico..."
                />
            </div>

            <div>
                <label htmlFor="anexo_url" className="block text-sm font-medium text-gray-700 mb-1">
                    URL do Anexo
                </label>
                <input
                    type="url"
                    id="anexo_url"
                    name="anexo_url"
                    value={formData.anexo_url}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="https://..."
                />
            </div>

            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default ExameForm;

